import React from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import DomUtils from "@tinqin/tinqin-utils/src/dom/index";
import ResponsiveUtils from "@tinqin/tinqin-utils/src/responsiveness/index";
import documentedProps from "./documentation/PopoverDocumentedProps";
import DeviceUtils from "@tinqin/tinqin-utils/src/devices";

import("./Popover.less");

export default class Popover extends React.Component {
    constructor(props) {
        super(props);
        this._recalculatePosition = this._recalculatePosition.bind(this);
        this._open = this._open.bind(this);
        this._close = this._close.bind(this);
        this._inViewport = this._inViewport.bind(this);
        this._calculateDimensions = this._calculateDimensions.bind(this);
        this._calculateDirection = this._calculateDirection.bind(this);
        this._move = this._move.bind(this);
        this._removePosClass = this._removePosClass.bind(this);
        this.visible = true;
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.popoverType === "keep") {
            this.keep = true;
        } else {
            this.keep = false;
        }
        if (this.content) {
            let minWidth = "";
            if (nextProps.anchor && nextProps.minWidthToAnchor) {
                //Check if the anchor is React Reference get the DOM element behind it.
                let nextPropsAnchor = nextProps.anchor.current || nextProps.anchor;
                let anchorRect = ResponsiveUtils.getBoundingClientRectObject(nextPropsAnchor);
                minWidth = anchorRect.width + "px";
            }

            this.content.style.width = "";
            this.content.style.minWidth = minWidth;
        }
        if (!nextProps.active) {
            this._close();
        }
    }

    componentWillUnmount() {
        document.removeEventListener("scroll", this._recalculatePosition, true);
        window.removeEventListener("resize", this._recalculatePosition);
    }

    componentDidUpdate() {
        if (this.props.active && this.content) {
            this._open();
        } else {
            if (this.open && this.content) {
                this._close();
                this.open = false;
            }
        }
    }

    getAnchor() {
        //if the anchor is React Reference we return the DOM element related to it,
        //otherwise we assume that anchor is passed as DOM element.
        if (this.props.anchor && this.props.anchor.current) {
            return ReactDOM.findDOMNode(this.props.anchor.current);
        }
        return this.props.anchor;
    }

    _recalculatePosition(event) {
        if (event.type === "scroll" && this.props.closeOnScroll) {
            this.props.closeOnScroll(event);
        } else if (event.type === "resize" && this.props.closeOnResize) {
            this.props.closeOnResize(event);
        } else {
            let isScrollOutsidePopover = DomUtils.isClickOutside(event, this.content);
            if (isScrollOutsidePopover && this.content !== event.target) {
                if (this._inViewport()) {
                    this.content.classList.remove("tq-hidden");
                    this.content.classList.add("tq-visible");
                    this._move();
                } else {
                    this.content.classList.remove("tq-visible");
                    this.content.classList.add("tq-hidden");
                }
            }
        }
    }

    _open() {
        if (Popover._isInModal(this.getAnchor())) {
            this.content.classList.add("tq-in-modal");
        }

        // We want the events to trigger in the capturing phase. In some cases we have popovers
        // inside a "react-custom-scrollbars" container. Unfortunately "react-custom-scrollbars"
        // seems to stop the propagation of the scroll event. This prevents us from triggering
        // the "_recalculatePosition" function in the bubbling phase.
        document.removeEventListener("scroll", this._recalculatePosition, true);
        window.removeEventListener("resize", this._recalculatePosition);

        document.addEventListener("scroll", this._recalculatePosition, true);
        window.addEventListener("resize", this._recalculatePosition);
        this.open = true;
        if (Popover._visible(this.getAnchor())) {
            this.content.classList.remove("tq-hidden");
            //call anchor getBoundingClientRect and pass the result to _calculateDimensions function
            let anchorRect = ResponsiveUtils.getBoundingClientRectObject(this.getAnchor());
            let dimensions = this._calculateDimensions(anchorRect);
            if (!dimensions) {
                return;
            }
            //Note: below we assume that popover don't want to be narrower than its anchor!
            if (this.props.minWidthToAnchor) {
                this.content.style.minWidth = anchorRect.width + "px";
            }
            if (this.props.maxWidthToAnchor) {
                this.content.style.maxWidth = anchorRect.width + "px";
            }

            this.content.style.width = "";
            let contentWidth = dimensions.contentWidth + "px";
            if (this.content) {
                this.content.style.width = contentWidth;
                this._move();
            }
        }
    }

    _close() {
        document.removeEventListener("scroll", this._recalculatePosition, true);
        window.removeEventListener("resize", this._recalculatePosition);
        if (this.content) {
            this.content.classList.remove("tq-visible");
            this.content.classList.add("tq-hidden");
        }
    }

    //Checks if the element provided is currently on the screen.
    //If it is scrolled outside of the screen or hidden behind another element, returns false.
    _inViewport() {
        if (
            this.getAnchor().offsetWidth === 0 ||
            this.getAnchor().offsetHeight === 0 ||
            !this.open
        ) {
            return false;
        }
        let anchorRect = ResponsiveUtils.getBoundingClientRectObject(this.getAnchor());
        let windowWidth = window.innerWidth;
        let windowHeight = window.innerHeight;
        let midX = (anchorRect.left + anchorRect.right) / 2;
        let midY = (anchorRect.top + anchorRect.bottom) / 2;
        let inViewport =
            anchorRect.top >= 0 &&
            anchorRect.bottom <= windowHeight &&
            anchorRect.left >= 0 &&
            anchorRect.right <= windowWidth;

        if (!inViewport) {
            return false;
        }

        let topNode = document.elementFromPoint(midX, midY);
        let anchorIsOnTop = false;
        let contentIsOnTop = false;
        while (topNode && topNode.parentNode && !anchorIsOnTop && !contentIsOnTop) {
            anchorIsOnTop = topNode === this.getAnchor();
            contentIsOnTop = topNode === this.content;
            topNode = topNode.parentNode;
        }
        let visible = (anchorIsOnTop || contentIsOnTop) && inViewport;
        if (this.visible !== visible) {
            if (visible) {
                this.props.onEnterViewport && this.props.onEnterViewport();
            } else {
                this.props.onExitViewport && this.props.onExitViewport();
            }
        }
        this.visible = visible;

        return visible;
    }

    // Returns false if the element is hidden by setting its opacity to 0, display to none
    // or visibility to hidden.
    static _visible(element) {
        let targetElement = element;
        let visible = true;
        while (targetElement.parentNode && visible) {
            let computedStyle = window.getComputedStyle(targetElement);
            let cssOpacity = computedStyle.getPropertyValue("opacity");
            let cssDisplay = computedStyle.getPropertyValue("display");
            visible = cssOpacity !== "0" && cssDisplay !== "none";
            targetElement = targetElement.parentNode;
        }

        return visible;
    }

    //remove double call of getBoundingClientRect
    //pass anchor size outside
    _calculateDimensions(anchorRect) {
        if (!this.content) {
            return;
        }
        let contentRect = ResponsiveUtils.getBoundingClientRectObject(this.content);
        let anchorWidth = anchorRect.width;
        let computedStyle = window.getComputedStyle(this.content);
        let marginTop = parseFloat(computedStyle.marginTop.replace("px", ""));
        let marginBottom = parseFloat(computedStyle.marginBottom.replace("px", ""));
        let marginLeft = parseFloat(computedStyle.marginLeft.replace("px", ""));
        let marginRight = parseFloat(computedStyle.marginRight.replace("px", ""));
        let contentWidth;
        let contentHeight;
        contentWidth = contentRect.width + marginLeft + marginRight;
        if (this.props.minWidthToAnchor) {
            contentWidth = contentWidth < anchorWidth ? anchorWidth : contentWidth;
        } else if (this.props.maxWidthToAnchor) {
            contentWidth = contentWidth > anchorWidth ? anchorWidth : contentWidth;
        } else if (this.props.minWidthToAnchor && this.props.maxWidthToAnchor) {
            contentWidth = anchorWidth;
        }
        contentHeight = contentRect.height + marginTop + marginBottom;
        return { contentWidth, contentHeight };
    }

    // The direction from which the popup will appear.
    // The direction is calculated only when the popover is being opened.
    // During scroll and resize of an already open popup, the direction remains the same.
    _calculateDirection() {
        //call anchor getBoundingClientRect and pass the result to _calculateDimensions function
        let anchorRect = ResponsiveUtils.getBoundingClientRectObject(this.getAnchor());
        let dimensions = this._calculateDimensions(anchorRect);
        let {
            outOfBoundsTop,
            outOfBoundsAnchorTop,
            outOfBoundsHalfTop,
            outOfBoundsBottom,
            outOfBoundsAnchorBottom,
            outOfBoundsHalfBottom,
            outOfBoundsRight,
            outOfBoundsAnchorRight,
            outOfBoundsHalfRight,
            outOfBoundsLeft,
            outOfBoundsAnchorLeft,
            outOfBoundsHalfLeft
        } = ResponsiveUtils.isOutOfWindowViewport(anchorRect, {
            height: dimensions.contentHeight,
            width: dimensions.contentWidth
        });

        let preferredPosition = this.props.preferredPosition || "bottom";
        let direction = preferredPosition;
        let className;

        switch (preferredPosition) {
            case "top":
                className = "tq-top-pos";
                break;
            case "top-right":
                className = "tq-top-right-pos";
                break;
            case "top-left":
                className = "tq-top-left-pos";
                break;
            case "bottom":
                className = "tq-bottom-pos";
                break;
            case "bottom-right":
                className = "tq-bottom-right-pos";
                break;
            case "bottom-left":
                className = "tq-bottom-left-pos";
                break;
            case "left":
                className = "tq-left-pos";
                break;
            case "left-top":
                className = "tq-left-top-pos";
                break;
            case "left-bottom":
                className = "tq-left-bottom-pos";
                break;
            case "right":
                className = "tq-right-pos";
                break;
            case "right-top":
                className = "tq-right-top-pos";
                break;
            case "right-bottom":
                className = "tq-right-bottom-pos";
                break;
            default:
                className = "";
                break;
        }

        if (preferredPosition === "bottom-left") {
            className = "tq-bottom-left-pos";
            if (outOfBoundsBottom) {
                direction = "top-left";
                className = "tq-top-left-pos";
                if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight && outOfBoundsHalfRight) {
                    direction = "top-auto-right";
                    className = "tq-top-pos";
                } else if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight && outOfBoundsHalfLeft) {
                    direction = "top-auto-left";
                    className = "tq-top-pos";
                } else if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight) {
                    direction = "top";
                    className = "tq-top-pos";
                } else if (outOfBoundsAnchorLeft) {
                    direction = "top-right";
                    className = "tq-top-right-pos";
                }
            } else {
                if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight && outOfBoundsHalfRight) {
                    direction = "bottom-auto-right";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight && outOfBoundsHalfLeft) {
                    direction = "bottom-auto-left";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight) {
                    direction = "bottom";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsAnchorLeft) {
                    direction = "bottom-right";
                    className = "tq-bottom-right-pos";
                }
            }
        }
        if (preferredPosition === "bottom-right") {
            className = "tq-bottom-right-pos";
            if (outOfBoundsBottom) {
                direction = "top-right";
                className = "tq-top-right-pos";
                if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight && outOfBoundsHalfLeft) {
                    direction = "top-auto-left";
                    className = "tq-top-pos";
                } else if (
                    outOfBoundsAnchorLeft &&
                    outOfBoundsAnchorRight &&
                    outOfBoundsHalfRight
                ) {
                    direction = "top-auto-right";
                    className = "tq-top-pos";
                } else if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight) {
                    direction = "top";
                    className = "tq-top-pos";
                } else if (outOfBoundsTop && outOfBoundsAnchorRight) {
                    direction = "bottom-left";
                    className = "tq-bottom-left-pos";
                } else if (outOfBoundsAnchorRight) {
                    direction = "top-left";
                    className = "tq-top-left-pos";
                } else if (outOfBoundsTop) {
                    direction = "bottom-right";
                    className = "tq-bottom-right-pos";
                }
            } else {
                if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight && outOfBoundsHalfLeft) {
                    direction = "bottom-auto-left";
                    className = "tq-bottom-pos";
                } else if (
                    outOfBoundsAnchorLeft &&
                    outOfBoundsAnchorRight &&
                    outOfBoundsHalfRight
                ) {
                    direction = "bottom-auto-right";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight) {
                    direction = "bottom";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsAnchorRight) {
                    direction = "bottom-left";
                    className = "tq-bottom-left-pos";
                }
            }
        }
        if (preferredPosition === "bottom") {
            className = "tq-bottom-pos";
            if (outOfBoundsBottom) {
                direction = "top";
                className = "tq-top-pos";
                if (outOfBoundsHalfRight && outOfBoundsAnchorLeft) {
                    direction = "top-auto-right";
                    className = "tq-top-pos";
                } else if (outOfBoundsHalfLeft && outOfBoundsAnchorRight) {
                    direction = "top-auto-left";
                    className = "tq-top-pos";
                } else if (outOfBoundsHalfRight) {
                    direction = "top-left";
                    className = "tq-top-left-pos";
                } else if (outOfBoundsHalfLeft) {
                    direction = "top-right";
                    className = "tq-top-right-pos";
                } else if (outOfBoundsTop) {
                    direction = "bottom";
                    className = "tq-bottom-pos";
                }
            } else {
                if (outOfBoundsHalfRight && outOfBoundsAnchorLeft) {
                    direction = "bottom-auto-right";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsHalfLeft && outOfBoundsAnchorRight) {
                    direction = "bottom-auto-left";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsHalfRight) {
                    direction = "bottom-left";
                    className = "tq-bottom-left-pos";
                } else if (outOfBoundsHalfLeft) {
                    direction = "bottom-right";
                    className = "tq-bottom-right-pos";
                }
            }
        }
        if (preferredPosition === "top-left") {
            className = "tq-top-left-pos";
            if (outOfBoundsTop) {
                direction = "bottom-left";
                className = "tq-bottom-left-pos";
                if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight && outOfBoundsHalfRight) {
                    direction = "bottom-auto-right";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight && outOfBoundsHalfLeft) {
                    direction = "bottom-auto-left";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight) {
                    direction = "top";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsAnchorLeft) {
                    direction = "bottom-right";
                    className = "tq-bottom-right-pos";
                }
            } else {
                if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight && outOfBoundsHalfRight) {
                    direction = "top-auto-right";
                    className = "tq-top-pos";
                } else if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight && outOfBoundsHalfLeft) {
                    direction = "top-auto-left";
                    className = "tq-top-pos";
                } else if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight) {
                    direction = "top";
                    className = "tq-top-pos";
                } else if (outOfBoundsAnchorLeft) {
                    direction = "top-right";
                    className = "tq-top-right-pos";
                }
            }
        }
        if (preferredPosition === "top-right") {
            className = "tq-top-right-pos";
            if (outOfBoundsTop) {
                direction = "bottom-right";
                className = "tq-bottom-right-pos";
                if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight && outOfBoundsHalfLeft) {
                    direction = "bottom-auto-left";
                    className = "tq-bottom-pos";
                } else if (
                    outOfBoundsAnchorLeft &&
                    outOfBoundsAnchorRight &&
                    outOfBoundsHalfRight
                ) {
                    direction = "bottom-auto-right";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight) {
                    direction = "top";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsAnchorRight) {
                    direction = "bottom-left";
                    className = "tq-bottom-left-pos";
                }
            } else {
                if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight && outOfBoundsHalfLeft) {
                    direction = "top-auto-left";
                    className = "tq-top-pos";
                } else if (
                    outOfBoundsAnchorLeft &&
                    outOfBoundsAnchorRight &&
                    outOfBoundsHalfRight
                ) {
                    direction = "top-auto-right";
                    className = "tq-top-pos";
                } else if (outOfBoundsAnchorLeft && outOfBoundsAnchorRight) {
                    direction = "top";
                    className = "tq-top-pos";
                } else if (outOfBoundsAnchorRight) {
                    direction = "top-left";
                    className = "tq-top-left-pos";
                }
            }
        }
        if (preferredPosition === "top") {
            className = "tq-top-pos";
            if (outOfBoundsTop) {
                direction = "top";
                className = "tq-bottom-pos";
                if (outOfBoundsHalfRight && outOfBoundsAnchorLeft) {
                    direction = "bottom-auto-right";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsHalfLeft && outOfBoundsAnchorRight) {
                    direction = "bottom-auto-left";
                    className = "tq-bottom-pos";
                } else if (outOfBoundsHalfRight) {
                    direction = "bottom-left";
                    className = "tq-bottom-left-pos";
                } else if (outOfBoundsHalfLeft) {
                    direction = "bottom-right";
                    className = "tq-bottom-right-pos";
                }
            } else {
                if (outOfBoundsHalfLeft && outOfBoundsAnchorRight) {
                    direction = "top-auto-left";
                    className = "tq-top-pos";
                } else if (outOfBoundsHalfRight && outOfBoundsAnchorLeft) {
                    direction = "top-auto-right";
                    className = "tq-top-pos";
                } else if (outOfBoundsHalfLeft) {
                    direction = "top-right";
                    className = "tq-top-right-pos";
                } else if (outOfBoundsHalfRight) {
                    direction = "top-left";
                    className = "tq-top-left-pos";
                }
            }
        }
        if (preferredPosition === "right-top") {
            className = "tq-right-top-pos";
            if (outOfBoundsRight) {
                direction = "left-top";
                className = "tq-left-top-pos";
                if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom && outOfBoundsHalfTop) {
                    direction = "left-auto-top";
                    className = "tq-left-pos";
                } else if (
                    outOfBoundsAnchorTop &&
                    outOfBoundsAnchorBottom &&
                    outOfBoundsHalfBottom
                ) {
                    direction = "left-auto-bottom";
                    className = "tq-left-pos";
                } else if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom) {
                    direction = "left";
                    className = "tq-left-pos";
                } else if (outOfBoundsAnchorTop) {
                    direction = "left-bottom";
                    className = "tq-left-bottom-pos";
                }
            } else {
                if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom && outOfBoundsHalfTop) {
                    direction = "right-auto-top";
                    className = "tq-right-pos";
                } else if (
                    outOfBoundsAnchorTop &&
                    outOfBoundsAnchorBottom &&
                    outOfBoundsHalfBottom
                ) {
                    direction = "right-auto-bottom";
                    className = "tq-right-pos";
                } else if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom) {
                    direction = "right";
                    className = "tq-right-pos";
                } else if (outOfBoundsBottom) {
                    direction = "right-bottom";
                    className = "tq-right-bottom-pos";
                }
            }
        }
        if (preferredPosition === "right-bottom") {
            className = "tq-right-bottom-pos";
            if (outOfBoundsRight) {
                direction = "left-bottom";
                className = "tq-left-bottom-pos";
                if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom && outOfBoundsHalfBottom) {
                    direction = "left-auto-bottom";
                    className = "tq-left-pos";
                } else if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom && outOfBoundsHalfTop) {
                    direction = "left-auto-top";
                    className = "tq-left-pos";
                } else if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom) {
                    direction = "left";
                    className = "tq-left-pos";
                } else if (outOfBoundsAnchorBottom) {
                    direction = "left-top";
                    className = "tq-left-top-pos";
                }
            } else {
                if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom && outOfBoundsHalfBottom) {
                    direction = "right-auto-bottom";
                    className = "tq-right-pos";
                } else if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom && outOfBoundsHalfTop) {
                    direction = "right-auto-top";
                    className = "tq-right-pos";
                } else if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom) {
                    direction = "right";
                    className = "tq-right-pos";
                } else if (outOfBoundsTop) {
                    direction = "right-top";
                    className = "tq-right-top-pos";
                }
            }
        }
        if (preferredPosition === "right") {
            className = "tq-right-pos";
            if (outOfBoundsRight) {
                direction = "left";
                className = "tq-left-pos";
                if (outOfBoundsHalfBottom && outOfBoundsAnchorTop) {
                    direction = "left-auto-bottom";
                    className = "tq-left-pos";
                } else if (outOfBoundsHalfTop && outOfBoundsAnchorBottom) {
                    direction = "left-auto-top";
                    className = "tq-left-pos";
                } else if (outOfBoundsHalfBottom) {
                    direction = "left-top";
                    className = "tq-left-top-pos";
                } else if (outOfBoundsHalfTop) {
                    direction = "left-bottom";
                    className = "tq-left-bottom-pos";
                }
            } else {
                if (outOfBoundsHalfBottom && outOfBoundsAnchorTop) {
                    direction = "right-auto-bottom";
                    className = "tq-right-pos";
                } else if (outOfBoundsHalfTop && outOfBoundsAnchorBottom) {
                    direction = "right-auto-top";
                    className = "tq-right-pos";
                } else if (outOfBoundsHalfBottom) {
                    direction = "right-bottom";
                    className = "tq-right-top-pos";
                } else if (outOfBoundsHalfTop) {
                    direction = "right-top";
                    className = "tq-right-bottom-pos";
                }
            }
        }
        if (preferredPosition === "left-top") {
            className = "tq-left-top-pos";
            if (outOfBoundsLeft) {
                direction = "right-top";
                className = "tq-right-top-pos";
                if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom && outOfBoundsHalfTop) {
                    direction = "right-auto-top";
                    className = "tq-right-pos";
                } else if (
                    outOfBoundsAnchorTop &&
                    outOfBoundsAnchorBottom &&
                    outOfBoundsHalfBottom
                ) {
                    direction = "right-auto-bottom";
                    className = "tq-right-pos";
                } else if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom) {
                    direction = "right";
                    className = "tq-right-pos";
                } else if (outOfBoundsAnchorTop) {
                    direction = "right-bottom";
                    className = "tq-right-bottom-pos";
                }
            } else {
                if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom && outOfBoundsHalfTop) {
                    direction = "left-auto-top";
                    className = "tq-left-pos";
                } else if (
                    outOfBoundsAnchorTop &&
                    outOfBoundsAnchorBottom &&
                    outOfBoundsHalfBottom
                ) {
                    direction = "left-auto-bottom";
                    className = "tq-left-pos";
                } else if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom) {
                    direction = "left";
                    className = "tq-left-pos";
                } else if (outOfBoundsBottom) {
                    direction = "left-bottom";
                    className = "tq-left-bottom-pos";
                }
            }
        }
        if (preferredPosition === "left-bottom") {
            className = "tq-left-bottom-pos";
            if (outOfBoundsLeft) {
                direction = "right-bottom";
                className = "tq-right-bottom-pos";
                if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom && outOfBoundsHalfBottom) {
                    direction = "right-auto-bottom";
                    className = "tq-right-pos";
                } else if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom && outOfBoundsHalfTop) {
                    direction = "right-auto-top";
                    className = "tq-right-pos";
                } else if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom) {
                    direction = "right";
                    className = "tq-right-pos";
                } else if (outOfBoundsAnchorBottom) {
                    direction = "right-top";
                    className = "tq-right-top-pos";
                }
            } else {
                if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom && outOfBoundsHalfBottom) {
                    direction = "left-auto-bottom";
                    className = "tq-left-pos";
                } else if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom && outOfBoundsHalfTop) {
                    direction = "left-auto-top";
                    className = "tq-left-pos";
                } else if (outOfBoundsAnchorTop && outOfBoundsAnchorBottom) {
                    direction = "left";
                    className = "tq-left-pos";
                } else if (outOfBoundsTop) {
                    direction = "left-top";
                    className = "tq-left-top-pos";
                }
            }
        }
        if (preferredPosition === "left") {
            className = "tq-left-pos";
            if (outOfBoundsLeft) {
                direction = "left";
                className = "tq-left-pos";
                if (outOfBoundsHalfBottom && outOfBoundsAnchorTop) {
                    direction = "right-auto-bottom";
                    className = "tq-right-pos";
                } else if (outOfBoundsHalfTop && outOfBoundsAnchorBottom) {
                    direction = "right-auto-top";
                    className = "tq-right-pos";
                } else if (outOfBoundsHalfBottom) {
                    direction = "right-top";
                    className = "tq-right-top-pos";
                } else if (outOfBoundsHalfTop) {
                    direction = "right-bottom";
                    className = "tq-right-bottom-pos";
                }
            } else {
                if (outOfBoundsHalfBottom && outOfBoundsAnchorTop) {
                    direction = "left-auto-bottom";
                    className = "tq-left-pos";
                } else if (outOfBoundsHalfTop && outOfBoundsAnchorBottom) {
                    direction = "left-auto-top";
                    className = "tq-left-pos";
                } else if (outOfBoundsHalfBottom) {
                    direction = "left-bottom";
                    className = "tq-left-top-pos";
                } else if (outOfBoundsHalfTop) {
                    direction = "left-top";
                    className = "tq-left-bottom-pos";
                }
            }
        }

        return { direction, className };
    }

    _move(event) {
        //call anchor getBoundingClientRect and pass the result to _calculateDimensions function
        let anchorRect = ResponsiveUtils.getBoundingClientRectObject(this.getAnchor());
        let dimensions = this._calculateDimensions(anchorRect);
        let anchorTop = anchorRect.top;
        if (DeviceUtils.isIOSMobileDevice()) {
            anchorTop = anchorRect.top + window.scrollY;
        }
        let anchorLeft = anchorRect.left;
        let anchorBottom = anchorTop + anchorRect.height;
        let anchorRight = anchorLeft + anchorRect.width;
        let anchorCenterX = anchorLeft + anchorRect.width / 2;
        let anchorCenterY = anchorTop + anchorRect.height / 2;
        let directionOptions = this._calculateDirection();
        //scroll inside of the popoverContainer, no need to move
        if (event && Popover._isInNode(this.content, event.target)) {
            return;
        }

        //If the anchor is hidden by css, close the popover, otherwise calculate its place.
        //This is done because if hide the anchor, for example when it's only visible on hover,
        //we want the popover to be closed.
        let directions;
        switch (directionOptions.direction) {
            case "top":
                directions = {
                    top: anchorTop - dimensions.contentHeight - 2 + "px",
                    left: anchorCenterX - dimensions.contentWidth / 2 + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "top-right":
                directions = {
                    top: anchorTop - dimensions.contentHeight - 2 + "px",
                    left: anchorLeft + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "top-left":
                directions = {
                    top: anchorTop - dimensions.contentHeight - 2 + "px",
                    left: anchorRight - dimensions.contentWidth + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "top-auto-right":
                directions = {
                    top: anchorTop - dimensions.contentHeight - 2 + "px",
                    left: "",
                    right: "0px",
                    bottom: ""
                };
                break;
            case "top-auto-left":
                directions = {
                    top: anchorTop - dimensions.contentHeight - 2 + "px",
                    left: "0px",
                    right: "",
                    bottom: ""
                };
                break;
            case "bottom":
                directions = {
                    top: anchorBottom + "px",
                    left: anchorCenterX - dimensions.contentWidth / 2 + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "bottom-right":
                directions = {
                    top: anchorBottom + "px",
                    left: anchorLeft + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "bottom-left":
                directions = {
                    top: anchorBottom + "px",
                    left: anchorRight - dimensions.contentWidth + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "bottom-auto-right":
                directions = {
                    top: anchorBottom + "px",
                    left: "",
                    right: "0px",
                    bottom: ""
                };
                break;
            case "bottom-auto-left":
                directions = {
                    top: anchorBottom + "px",
                    left: "0px",
                    right: "",
                    bottom: ""
                };
                break;
            case "left":
                directions = {
                    top: anchorCenterY - dimensions.contentHeight / 2 + "px",
                    left: anchorLeft - dimensions.contentWidth - 2 + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "left-top":
                directions = {
                    top: anchorTop + "px",
                    left: anchorLeft - dimensions.contentWidth + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "left-bottom":
                directions = {
                    top: anchorBottom - dimensions.contentHeight + "px",
                    left: anchorLeft - dimensions.contentWidth - 2 + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "left-auto-top":
                directions = {
                    top: "0px",
                    left: anchorLeft - dimensions.contentWidth + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "left-auto-bottom":
                directions = {
                    top: "",
                    left: anchorLeft - dimensions.contentWidth + "px",
                    right: "",
                    bottom: "0px"
                };
                break;
            case "right":
                directions = {
                    top: anchorCenterY - dimensions.contentHeight / 2 + "px",
                    left: anchorRight + 2 + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "right-top":
                directions = {
                    top: anchorTop + "px",
                    left: anchorRight + 2 + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "right-bottom":
                directions = {
                    top: anchorBottom - dimensions.contentHeight + "px",
                    left: anchorRight + 2 + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "right-auto-top":
                directions = {
                    top: "0px",
                    left: anchorRight + 2 + "px",
                    right: "",
                    bottom: ""
                };
                break;
            case "right-auto-bottom":
                directions = {
                    top: "",
                    left: anchorRight + 2 + "px",
                    right: "",
                    bottom: "0px"
                };
                break;
            default:
                break;
        }

        if (this.props.offsetTop && directions.top) {
            directions.top =
                Number(directions.top.slice(0, -2)) + Number(this.props.offsetTop) + "px";
        }
        if (this.props.offsetLeft && directions.left) {
            directions.left =
                Number(directions.left.slice(0, -2)) + Number(this.props.offsetLeft) + "px";
        }
        if (this.props.offsetRight && directions.right) {
            directions.right =
                Number(directions.right.slice(0, -2)) + Number(this.props.offsetRight) + "px";
        }
        if (this.props.offsetBottom && directions.bottom) {
            directions.bottom =
                Number(directions.bottom.slice(0, -2)) + Number(this.props.offsetBottom) + "px";
        }

        //Set the calculated dimensions:
        this.content.classList.add("tq-visible");
        this._removePosClass();
        this.content.classList.add(directionOptions.className);
        this.content.style.top = directions.top;
        this.content.style.left = directions.left;
        this.content.style.right = directions.right;
        if (DeviceUtils.isIOSMobileDevice()) {
            this.content.style.position = "absolute";
        }
    }

    _removePosClass() {
        this.content.classList.remove("tq-top-pos");
        this.content.classList.remove("tq-top-right-pos");
        this.content.classList.remove("tq-top-left-pos");
        this.content.classList.remove("tq-bottom-pos");
        this.content.classList.remove("tq-bottom-right-pos");
        this.content.classList.remove("tq-bottom-left-pos");
        this.content.classList.remove("tq-left-pos");
        this.content.classList.remove("tq-left-top-pos");
        this.content.classList.remove("tq-left-bottom-pos");
        this.content.classList.remove("tq-right-pos");
        this.content.classList.remove("tq-right-top-pos");
        this.content.classList.remove("tq-right-bottom-pos");
    }

    static _isInModal(child) {
        let node = child.parentNode;
        while (node !== null) {
            if ((" " + node.className + " ").indexOf(" tq-modal-container ") > -1) {
                return true;
            }
            node = node.parentNode;
        }
        return false;
    }

    static _isInNode(parent, node) {
        while (node !== null) {
            if (parent === node) {
                return true;
            }
            node = node.parentNode;
        }
        return false;
    }

    render() {
        let PopoverClass = "tq-popover-placeholder ";
        PopoverClass += this.props.active ? "tq-active " : "";
        PopoverClass += this.props.extraClasses || "";

        if (this.props.active || this.props.popoverType === "load") {
            return ReactDOM.createPortal(
                <div
                    ref={(element) => (this.content = element && element.childNodes[0])}
                    className={PopoverClass}
                >
                    {this.props.children}
                </div>,
                document.body
            );
        } else if (this.keep) {
            return ReactDOM.createPortal(
                <div
                    ref={(element) => (this.content = element && element.childNodes[0])}
                    className={PopoverClass}
                >
                    {this.props.children}
                </div>,
                document.body
            );
        } else {
            return null;
        }
    }
}

Popover.displayName = "Popover";

Popover.propTypes = {
    active: PropTypes.bool,
    anchor: PropTypes.object,
    extraClasses: PropTypes.string,
    popoverType: PropTypes.oneOf(["default", "keep", "load"]),
    offsetTop: PropTypes.number,
    offsetLeft: PropTypes.number,
    offsetRight: PropTypes.number,
    offsetBottom: PropTypes.number,
    minWidthToAnchor: PropTypes.bool,
    maxWidthToAnchor: PropTypes.bool,
    closeOnScroll: PropTypes.func,
    closeOnResize: PropTypes.func,
    onEnterViewport: PropTypes.func,
    onExitViewport: PropTypes.func,
    preferredPosition: PropTypes.oneOf([
        "top",
        "top-right",
        "top-left",
        "bottom",
        "bottom-right",
        "bottom-left",
        "left",
        "left-top",
        "left-bottom",
        "right",
        "right-top",
        "right-bottom"
    ])
};
Popover.documentedProps = documentedProps;
